import React, { useState } from 'react';
import { addDays, format } from 'date-fns';
import { inject } from 'mobx-react';
import {
  CheckmarkSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '../../icons';
import MyPageButton from '../../components/MyPageButton';
import './ProductCard.css';

// --- Helper Functions ---
const isAirFeatured = (product) =>
  product.features.some((feature) => feature.node.name === 'air');

const isExpiring = (subscription) => {
  return (
    subscription &&
    subscription.node &&
    new Date(subscription.node.end_date) < addDays(new Date(), 30)
  );
};

const isExpired = (subscription) => {
  return (
    subscription &&
    subscription.node &&
    new Date(subscription.node.end_date) < new Date()
  );
};

const isGrayed = (subscription) => subscription && !isExpiring(subscription);

const getProductPrice = (product) =>
  Number.parseFloat(
    product.productRatePlans[0].productRatePlanCharges.reduce(
      (acc, currentCharge) => acc + currentCharge.pricing[0].price,
      0
    )
  ).toFixed(2);

const getAirHeadImageSrc = (subscription) =>
  `products/air_head${isGrayed(subscription) ? '_expired' : ''}.png`;

// --- Reusable Helper Components (from previous refactor) ---

const CardMediaSection = inject('translationsStore')(
  ({
    // Inject here if translationsStore is needed directly
    logo,
    subscription,
    isDiscounted,
    translationsStore,
    type, // 'available', 'expiring', 'subscribed'
    logoDescription = '',
    badge = undefined, // Optional badge object
  }) => {
    const airHeadImageSrc = getAirHeadImageSrc(subscription);
    const productLogoSrc = `products/${logo}${
      type === 'subscribed' ? '_expired' : ''
    }.png`;

    const airHeadImg = <img alt='air_head' src={airHeadImageSrc} />;
    const productLogoImg = (
      <img
        alt='product_logo'
        src={productLogoSrc}
        style={{
          width: '70%',
          height: 'auto',
          margin: '0 auto',
          display: 'block',
        }}
      />
    );
    const LogoDescription = (
      <div
        style={{
          textAlign: 'center',
          fontSize: '18px',
          maxWidth: '80%',
          margin: '0 auto',
          marginTop: '2px',
          color: '#333',
          fontWeight: '600',
        }}
      >
        {logoDescription}
      </div>
    );

    if (type === 'subscribed') {
      return (
        <React.Fragment>
          {airHeadImg}
          {productLogoImg}
          {LogoDescription}
        </React.Fragment>
      );
    }

    return (
      <div>
        {isDiscounted && (
          <div className={'DiscountedBadge'}>
            {translationsStore.translate(
              'servicesView.serviceCard.discount.sale'
            )}
          </div>
        )}

        {!isDiscounted && badge && type === 'available' && (
          <div
            className={'DiscountedBadge'}
            style={{
              backgroundColor: badge.color,
              fontSize: badge.fontSize || 'inherit',
            }}
          >
            {badge.label}
          </div>
        )}
        {airHeadImg}
        {productLogoImg}
        {LogoDescription}
      </div>
    );
  }
);

const PriceDetails = inject('translationsStore')(
  ({
    // Inject here
    type,
    product,
    showPrice,
    isDiscounted,
    translationsStore,
    subscription,
  }) => {
    if (type === 'available') {
      if (!showPrice) return null;
      if (isDiscounted) {
        return (
          <div className='PromoPrice'>
            {translationsStore.translate(
              'servicesView.serviceCard.discount.promoPrice'
            )}
          </div>
        );
      }
      return (
        <div className='PriceLabel'>
          {translationsStore.translate('servicesView.serviceCard.price')}{' '}
          {getProductPrice(product)}
          {translationsStore.translate(
            'servicesView.serviceCard.currencyPlusTax'
          )}
        </div>
      );
    }

    if (type === 'expiring') {
      let priceSection;
      if (isDiscounted) {
        priceSection = (
          <div className='PromoPrice'>
            {translationsStore.translate(
              'servicesView.serviceCard.discount.promoPrice'
            )}
          </div>
        );
      } else if (isAirFeatured(product)) {
        priceSection = (
          <div className='PriceLabel'>
            {translationsStore.translate('servicesView.serviceCard.price')} 120€
            anno <br />o 12€ mese
          </div>
        );
      } else {
        priceSection = (
          <div className='PriceLabel'>
            {translationsStore.translate('servicesView.serviceCard.price')}{' '}
            {getProductPrice(product)}
            {translationsStore.translate(
              'servicesView.serviceCard.currencyPlusTax'
            )}
          </div>
        );
      }
      return (
        <React.Fragment>
          {priceSection}
          <div>
            {format(new Date(subscription?.node?.end_date), 'DD/MM/YYYY')}
          </div>
        </React.Fragment>
      );
    }
    return null;
  }
);

const CardActionButton = inject('translationsStore')(
  ({
    // Inject here
    product,
    onSelect,
    disabled,
    translationsStore,
    type,
  }) => {
    let titleKey;
    let defaultTitle;

    const isMoveInFeature = product.features.some(
      (feature) => feature.node.name === 'movein' && product.category === 'base'
    );
    if (type === 'available') {
      titleKey = isMoveInFeature
        ? 'servicesView.serviceCard.blackBoxSubmitButton'
        : 'servicesView.serviceCard.submitButton';
    } else if (type === 'expiring') {
      titleKey = isMoveInFeature
        ? 'servicesView.serviceCard.renewButton'
        : 'servicesView.serviceCard.submitButton';
    } else {
      return null;
    }

    return (
      <MyPageButton
        theme='primary'
        solid
        title={translationsStore.translate(titleKey, defaultTitle)}
        onClick={() => onSelect(product)}
        disabled={disabled}
      />
    );
  }
);

// --- NEW: Unified Front Card Component ---
const UnifiedFrontCard = inject('translationsStore')(
  ({
    cardType, // 'available', 'subscribed', 'expiring'
    product,
    logo,
    description, // Used by 'available'
    subscription,
    onSelect,
    disabled, // Used by 'available'
    renewDisabled, // Used by 'expiring'
    isDiscounted,
    variant,
    showPrice, // Used by 'available'
    logoDescription = '', // Optional description for the logo
    badge = undefined, // Optional badge object
    showBigPrice = false, // Used by 'available'
  }) => {
    const getCardContentClass = () => {
      if (cardType === 'available') {
        return 'Available';
      }
      if (cardType === 'expiring') {
        return 'Expiring';
      }
      return ''; // For 'subscribed' or default
    };

    const actionButtonDisabled =
      cardType === 'expiring' ? renewDisabled : disabled;

    // Get duration in months if available
    const durationInMonths = product?.duration_in_months || 0;

    // Calculate monthly price if product has duration
    const calculateMonthlyPrice = () => {
      if (!product || !durationInMonths) {
        return null;
      }

      const totalPrice = Number.parseFloat(
        product.productRatePlans[0].productRatePlanCharges.reduce(
          (acc, currentCharge) => acc + currentCharge.pricing[0].price,
          0
        )
      );

      return (totalPrice / durationInMonths).toFixed(2).replace('.', ','); // Format to use comma as decimal separator
    };

    const monthlyPrice = calculateMonthlyPrice();

    return (
      <React.Fragment>
        <CardMediaSection
          logo={logo}
          subscription={subscription}
          isDiscounted={isDiscounted}
          type={cardType}
          variant={variant}
          logoDescription={logoDescription} // Optional description for the logo
          badge={badge} // Pass badge if available
        />
        <div className={`CardContent ${getCardContentClass()}`}>
          {/* MoveIn Duration Display */}
          {showBigPrice && cardType === 'available' && durationInMonths > 0 && (
            <div
              style={{
                textAlign: 'center',
                fontSize: '36px',
                color: '#333',
                lineHeight: '1',
                marginBottom: '12px',
              }}
            >
              {durationInMonths}
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: 'normal',
                }}
              >
                mensilità
              </div>
              {monthlyPrice && (
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 'normal',
                    color: '#666',
                    marginTop: '8px',
                  }}
                >
                  {monthlyPrice}€/mese
                </div>
              )}
            </div>
          )}

          {//show description only if description is set and if it's showBigPrice case when cardType is not 'available'
          description &&
            ((cardType !== 'available' && showBigPrice) || !showBigPrice) && (
              <div
                className='TextContainer'
                style={{
                  textAlign: 'center',
                  color: '#ababab',
                  fontSize: '17px',
                }}
              >
                <p>{description}</p>
              </div>
            )}

          {/* Status and Price Section */}

          {cardType === 'available' && showPrice && (
            <>
              {showBigPrice ? (
                <div
                  className='Price'
                  style={{
                    fontWeight: '700',
                    fontSize: '24px',
                    marginBottom: '8px',
                  }}
                >
                  {getProductPrice(product)}€
                </div>
              ) : (
                <div className='Price'>
                  <PriceDetails
                    type='available'
                    product={product}
                    showPrice={true}
                    isDiscounted={isDiscounted}
                  />
                </div>
              )}
            </>
          )}

          {cardType === 'subscribed' && (
            <div>
              <p className='ActiveLabel'>Attivo</p>
              <p className='Price'>
                Scade il {format(subscription.node.end_date, 'DD/MM/YYYY')}
              </p>
            </div>
          )}

          {cardType === 'expiring' && (
            <div>
              {!isExpired(subscription) && (
                <p className='ActiveLabel'>Attivo</p>
              )}
              <p className='ExpiringLabel'>
                {isExpired(subscription) ? 'Scaduto' : 'In scadenza'}
              </p>
              <div className='Price'>
                <PriceDetails
                  type='expiring'
                  product={product}
                  isDiscounted={isDiscounted}
                  // translationsStore={translationsStore} // Injected
                  subscription={subscription}
                />
              </div>
            </div>
          )}

          {/* Action Button */}
          {(cardType === 'available' || cardType === 'expiring') && (
            <CardActionButton
              type={cardType}
              product={product}
              onSelect={onSelect}
              disabled={actionButtonDisabled}
              // translationsStore={translationsStore} // Injected
            />
          )}
        </div>
      </React.Fragment>
    );
  }
);

const CardFront = (props) => {
  // No longer needs inject('translationsStore') here
  const { subscription } = props;
  let cardType;
  if (isExpiring(subscription)) {
    cardType = 'expiring';
  } else if (isGrayed(subscription)) {
    cardType = 'subscribed';
  } else {
    cardType = 'available';
  }

  return <UnifiedFrontCard cardType={cardType} {...props} />;
};

// --- CardBack (Updated to use getAirHeadImageSrc, from previous refactor) ---
const CardBack = inject('translationsStore')(
  ({ product, subscription, isFront, onFlip, translationsStore }) => (
    <div className={isFront ? 'CardBack closed' : 'CardBack'}>
      <div className='CardBackHeader' onClick={onFlip}>
        + info{' '}
        {isFront ? (
          <ChevronDownIcon className='BackIcon' width={12} height={12} />
        ) : (
          <ChevronUpIcon className='BackIcon' width={12} height={12} />
        )}
      </div>
      <img alt='product_air_head_back' src={getAirHeadImageSrc(subscription)} />
      <div className='CardContent'>
        <div className='TextContainer'>
          {product.features
            .map((feature) =>
              translationsStore.translate(
                'servicesView.serviceCard.features.' + feature.node.name,
                feature.node.name // Fallback text
              )
            )
            .flat()
            .map((featureText, index) => (
              <div key={index} className='ListItemContainer'>
                <div className='ListIcon'>
                  <CheckmarkSquareIcon width={16} height={16} />
                </div>
                <p className='Description'>{featureText}</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
);

/**
 * @param {object} props - The component's props.
 * @param {Array<object>} props.subscriptions - Array of subscription objects. Each subscription object
 *                                              should typically have a `product.uuid` and a `node.end_date`.
 * @param {object} props.product - The product data object. Expected to have:
 *                                 `uuid` (string),
 *                                 `features` (array of objects with `node.name`),
 *                                 `category` (string, e.g., 'base'),
 *                                 `productRatePlans` (array with `productRatePlanCharges` which have `pricing`).
 * @param {string} props.logo - The filename (without extension) for the product logo image.
 * @param {string} props.description - Text description of the product.
 * @param {function} props.onSelect - Callback function triggered when the main action button is clicked.
 *                                    Receives the `product` object as an argument.
 * @param {boolean} [props.disabled=false] - (Optional) If true, disables the action button for "available" products.
 * @param {boolean} [props.renewDisabled=false] - (Optional) If true, disables the action button for "expiring" products.
 * @param {boolean} [props.isDiscounted=false] - (Optional) If true, indicates the product has a discount, affecting display.
 * @param {string} [props.variant] - (Optional) A string to control variations in media rendering (e.g., specific image styles).
 *                                   (Currently a TODO for full implementation in CardMediaSection).
 * @param {boolean} [props.showPrice=true] - (Optional) Controls visibility of the price section for "available" products.
 * @param {string} [props.logoDescription=''] - (Optional) Description for the logo image, used in CardMediaSection.
 * @param {object} [props.badge] - (Optional) Badge object with `color`, `label`, and optional `fontSize` for special product badges.
 * @param {boolean} [props.showBigPrice=false] - (Optional) If true, displays a larger price section for "available" products.
 * @returns {JSX.Element} The rendered ProductCard component.
 */
const ProductCardBaseNew = ({ subscriptions, ...otherProps }) => {
  const [isFront, setIsFront] = useState(true);

  const subscription =
    !!subscriptions &&
    subscriptions.find((sub) => sub.product.uuid === otherProps.product.uuid);

  // Font style that resembles Inter using system fonts
  const interLikeStyle = {
    fontFamily:
      "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  };

  return (
    <div className='ProductCard' style={interLikeStyle}>
      <CardFront {...otherProps} subscription={subscription} />
      <CardBack
        {...otherProps}
        subscription={subscription}
        isFront={isFront}
        onFlip={() => setIsFront(!isFront)}
      />
    </div>
  );
};

export default ProductCardBaseNew; // Or export inject('translationsStore')(ProductCardBaseNew) if needed
